import React, { Suspense, useContext, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom/cjs/react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { TostMsg } from "../../../App";
import { addProjectSprint, getProjectList } from "./Service/TaskService";
import { ProjectModalContext } from "./Service/ProjectModalContext";
import ComponentLoader from "../../../Shared/Sidebar/ComponentLoader";

function CreateSprints({ ProjectId = 0, SprintNumber = 2, ProjectName = "OrgGenie Mobile App Platform" }) {
  const history = useHistory();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const toster = useContext(TostMsg);
  const editingSprint = location?.state?.editingSprint || null;
  const isEditMode = Boolean(location?.state?.isEdit && editingSprint);
  const { selectedProject, updateSelectedProject, refreshSelectedProject } = useContext(ProjectModalContext);
  const [endDateManuallySet, setEndDateManuallySet] = useState(false);

  // Extract values from selectedProject or use defaults
  const projectId = selectedProject?.Id || selectedProject?.ProjectId || ProjectId;
  const projectName = selectedProject?.Name || ProjectName;
  const sprintNumber = isEditMode
    ? editingSprint?.SprintNumber || SprintNumber
    : selectedProject?.ProjectSprints?.CompletedSprints?.length
      ? selectedProject.ProjectSprints.CompletedSprints.length + 1
      : SprintNumber;

  const initialFormValues = {
    ProjectId: projectId,
    SprintName: editingSprint?.SprintName || "",
    SprintGoal: editingSprint?.SprintGoal || "",
    PlannedStartDate: editingSprint?.PlannedStartDate?.slice(0, 10) || "",
    PlannedEndDate: editingSprint?.PlannedEndDate?.slice(0, 10) || "",
  };

  const SprintValidation = Yup.object().shape({
    SprintName: Yup.string()
      .trim()
      .required("Sprint name is required")
      .max(100, "Sprint name must be at most 100 characters"),
    PlannedStartDate: Yup.string().required("Start date is required"),
    PlannedEndDate: Yup.string().required("End date is required")
      .test(
        "end-after-start",
        "End date must be after start date",
        function (value) {
          const { PlannedStartDate } = this.parent;
          if (!PlannedStartDate || !value) return true;
          return new Date(value) > new Date(PlannedStartDate);
        }
      ),
    SprintGoal: Yup.string().max(500, "Sprint goal must be at most 500 characters"),
  });

  const sprintForm = useFormik({
    enableReinitialize: true,
    initialValues: initialFormValues,
    validationSchema: SprintValidation,
    // AFTER:
    onSubmit: async (data, { resetForm }) => {
      setLoading(true);
      const body = {
        ID: editingSprint?.Id || editingSprint?.ID || 0,
        Guid: editingSprint?.Guid || editingSprint?.GUID || "",
        ProjectId: data.ProjectId,
        SprintName: data.SprintName.trim(),
        SprintGoal: data.SprintGoal.trim(),
        PlannedStartDate: data.PlannedStartDate,
        PlannedEndDate: data.PlannedEndDate,
      };
      try {
        const result = await addProjectSprint(body);
        setLoading(false);
        toster.current.success(
          result?.Data?.Message || (isEditMode ? "Sprint updated successfully!" : "Sprint created successfully!")
        );

        if (isEditMode) {
          refreshSelectedProject();
          resetForm();
          history.goBack();
          return;
        }
        try {
          const { getProjectList } = await import("./Service/TaskService");
          const res = await getProjectList({
            Id: 0,
            StartDate: "",
            EndDate: "",
            ProjectStatusId: "",
            PageNumber: 1,
            PageSize: 1000,
            Search: "",
            ProjectTypeId: "",
            ClientId: "",
            SortBy: "",
            SortOrder: "",
          });

          const projectList = res?.Data?.ProjectList || [];
          const updatedProject = projectList.find(p => p.Id === projectId);
          refreshSelectedProject();
          resetForm();

          if (updatedProject) {
            const allSprints = [
              ...(updatedProject.ProjectSprints?.ActiveSprint ? [updatedProject.ProjectSprints.ActiveSprint] : []),
              ...(updatedProject.ProjectSprints?.PlanningSprint || []),
            ];

            const planningSprints = allSprints
              .filter(s => s.SprintStatusId === 1)
              .sort((a, b) => new Date(b.CreatedDate) - new Date(a.CreatedDate));

            const newSprint = planningSprints[0] || null;

            history.replace({
              pathname: "/Project/Sprint",
              state: {
                view: "planning",
                sprint: newSprint,
              },
            });
          } else {
            history.replace("/Project/Dashboard");
          }
        } catch (fetchError) {
          setLoading(false);
          refreshSelectedProject();
          resetForm();
          history.replace("/Project/Dashboard");
        }

      } catch (error) {
        setLoading(false);
        toster.current.error(
          error?.response?.data?.Message || error?.result?.Data?.Message || "Something went wrong"
        );
      } finally {
        setLoading(false);
      }
    },
  });


  return (
    <div className="sprint-section-area">
      <div className="task-top-pane">
        <i className="fa fa-thumb-tack i-ico" aria-hidden="true"></i>
        <div className="task-top-pane-title">
          <h4>Welcome to OrgGenie</h4>
          <p>Manage your tasks, projects, and workspaces in one place.</p>
        </div>
      </div>
      <div className="tm-main-area">
        <div className="page-head">
          <div>
            <h2>{isEditMode ? "Edit Sprint" : "Create Sprint"}</h2>
            <p>{isEditMode ? `Update sprint ${sprintNumber} for ${projectName}` : `This will create new`}</p>
          </div>
          <div className="head-actions">
            <div className="flex ic gap8">
              <Link
                to="/Project/Dashboard"
                className="btn-og btn-og-secondary btn-og-sm"
                style={{ display: "flex", justifyContent: "flex-start" }}
                title="Back"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  className="bi bi-arrow-left"
                  viewBox="0 0 16 16"
                >
                  <path
                    fillRule="evenodd"
                    d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8"
                  />
                </svg>
              </Link>
            </div>
          </div>
        </div>
        <div className="page">
          <div className="row justify-content-start">
            <div className="col-lg-9">
              <form className="create-form-wrap" onSubmit={sprintForm.handleSubmit} noValidate>

                {/* Sprint Name */}
                <div className="form-group">
                  <label htmlFor="SprintName">
                    Sprint Name <span className="req">*</span>
                  </label>
                  <input
                    className={`form-control ${sprintForm.touched.SprintName && sprintForm.errors.SprintName ? "is-invalid" : ""}`}
                    id="SprintName"
                    name="SprintName"
                    placeholder="e.g. Sprint 4"
                    autoComplete="off"
                    value={sprintForm.values.SprintName}
                    onChange={sprintForm.handleChange}
                    onBlur={sprintForm.handleBlur}
                  />
                  {sprintForm.touched.SprintName && sprintForm.errors.SprintName && (
                    <div className="invalid-feedback">{sprintForm.errors.SprintName}</div>
                  )}
                </div>

                {/* Start Date / End Date */}
                <div className="form-row-2">
                  <div>
                    <label htmlFor="PlannedStartDate">
                      Start Date <span className="req">*</span>
                    </label>
                    <input
                      type="date"
                      className={`form-control ${sprintForm.touched.PlannedStartDate && sprintForm.errors.PlannedStartDate ? "is-invalid" : ""}`}
                      id="PlannedStartDate"
                      name="PlannedStartDate"
                      value={sprintForm.values.PlannedStartDate}
                      onChange={(e) => {
                        sprintForm.handleChange(e);
                        if (!isEditMode && !endDateManuallySet) {
                          const startDate = new Date(e.target.value);
                          if (!isNaN(startDate)) {
                            const endDate = new Date(startDate);
                            endDate.setDate(endDate.getDate() + 14);
                            sprintForm.setFieldValue("PlannedEndDate", endDate.toISOString().slice(0, 10));
                          }
                        }
                      }}
                      onBlur={sprintForm.handleBlur}
                      onClick={(e) => e.target.showPicker && e.target.showPicker()}
                    />
                    {sprintForm.touched.PlannedStartDate && sprintForm.errors.PlannedStartDate && (
                      <div className="invalid-feedback">{sprintForm.errors.PlannedStartDate}</div>
                    )}
                  </div>

                  <div>
                    <label htmlFor="PlannedEndDate">
                      End Date <span className="req">*</span>
                    </label>
                    <input
                      type="date"
                      className={`form-control ${sprintForm.touched.PlannedEndDate && sprintForm.errors.PlannedEndDate ? "is-invalid" : ""}`}
                      id="PlannedEndDate"
                      name="PlannedEndDate"
                      value={sprintForm.values.PlannedEndDate}
                      onChange={(e) => {
                        setEndDateManuallySet(true);
                        sprintForm.handleChange(e);
                      }}
                      onBlur={sprintForm.handleBlur}
                      onClick={(e) => e.target.showPicker && e.target.showPicker()}
                    />
                    {sprintForm.touched.PlannedEndDate && sprintForm.errors.PlannedEndDate && (
                      <div className="invalid-feedback">{sprintForm.errors.PlannedEndDate}</div>
                    )}
                  </div>
                </div>

                {/* Sprint Goal */}
                <div className="form-group">
                  <label htmlFor="SprintGoal">
                    Sprint Goal{" "}
                    <span style={{ fontWeight: "400", color: "#9ca3af" }}>(optional)</span>
                  </label>
                  <textarea
                    className={`form-control form-textarea ${sprintForm.touched.SprintGoal && sprintForm.errors.SprintGoal ? "is-invalid" : ""}`}
                    id="SprintGoal"
                    name="SprintGoal"
                    placeholder="What does this sprint aim to deliver?"
                    value={sprintForm.values.SprintGoal}
                    onChange={sprintForm.handleChange}
                    onBlur={sprintForm.handleBlur}
                  />
                  {sprintForm.touched.SprintGoal && sprintForm.errors.SprintGoal && (
                    <div className="invalid-feedback">{sprintForm.errors.SprintGoal}</div>
                  )}
                </div>

                {/* Actions */}
                <div className="form-actions" id="create-form-actions">
                  <Link
                    to="/Project/Dashboard"
                    className="btn-og btn-og-secondary btn-og-sm"
                  >
                    Cancel
                  </Link>

                  <button
                    type="submit"
                    className="btn btn-brand btn-sm"
                  // disabled={loading || !(sprintForm.isValid && sprintForm.dirty)}
                  >
                    {loading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-1"
                          role="status"
                          aria-hidden="true"
                        />
                        Saving…
                      </>
                    ) : (
                      <>
                        <i className="fa-regular fa-clock"></i> Save
                      </>
                    )}
                  </button>
                </div>

              </form>
            </div>
          </div>
        </div>
      </div>

      {(loading) && (
        <Suspense>
          <ComponentLoader />
        </Suspense>
      )}
    </div>
  )
}

export default CreateSprints